import { usePageviews } from "@/lib/pageviews";
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieNotice from "@/components/CookieNotice";
import MetaPixel from "@/components/MetaPixel";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Team from "@/pages/Team";
import TeamProfile from "@/pages/TeamProfile";
import TreatmentsHub from "@/pages/TreatmentsHub";
import TreatmentPage from "@/pages/TreatmentPage";
import ConcernsHub from "@/pages/ConcernsHub";
import ConcernFamily from "@/pages/ConcernFamily";
import ConcernPage from "@/pages/ConcernPage";
import FirstVisit from "@/pages/FirstVisit";
import Pricing from "@/pages/Pricing";
import Book from "@/pages/Book";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import HairLanding from "@/pages/HairLanding";
import HairAssessment from "@/pages/HairAssessment";
import HairPrp from "@/pages/HairPrp";
import TransplantHub from "@/pages/TransplantHub";
import TransplantSub from "@/pages/TransplantSub";
import HairLossHub from "@/pages/HairLossHub";
import HairConcern from "@/pages/HairConcern";
import HairDoctor from "@/pages/HairDoctor";
import HairJournal from "@/pages/HairJournal";
import HairBooking from "@/pages/HairBooking";
import { HairHeader, HairFooter } from "@/components/HairShared";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import { ConciergeProvider } from "@/components/Concierge";
import { getTransplantSubPage } from "@/data/hair";
import { LEGACY_HAIR_SLUGS } from "@/data/concerns";
import { AD_PATHS } from "@/data/adLanding";
import AdLanding from "@/pages/AdLanding";
import Register from "@/pages/Register";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const HairTransplantRedirect = () => {
  const params = useParams();
  const splat = params["*"] || params.subSlug || "";
  const target = getTransplantSubPage(splat) ? `/hair/transplantation/${splat}` : "/hair/transplantation";
  return <Navigate to={target} replace />;
};

const LegacyHairConcernRedirect = () => {
  const { slug } = useParams();
  const next = LEGACY_HAIR_SLUGS[slug];
  return <Navigate to={next ? `/hair/hair-loss/${next}` : "/hair/hair-loss"} replace />;
};

const isHairPath = (pathname) => /^\/hair(\/|$)/.test(pathname);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isBare = isAdmin || AD_PATHS.includes(pathname);
  const isHair = isHairPath(pathname);
  usePageviews();
  return (
    <>
      <ConciergeProvider>
      <ScrollManager />
      {!isBare && (isHair ? <HairHeader /> : <Header />)}
      <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/:slug" element={<TeamProfile />} />
            <Route path="/treatments" element={<TreatmentsHub />} />
            <Route path="/treatments/:slug" element={<TreatmentPage />} />
            <Route path="/concerns" element={<ConcernsHub />} />
            <Route path="/concerns/:family" element={<ConcernFamily />} />
            <Route path="/concerns/:family/:slug" element={<ConcernPage />} />
            <Route path="/hair" element={<HairLanding />} />
            <Route path="/hair/hair-loss" element={<HairLossHub />} />
            <Route path="/hair/hair-loss/:slug" element={<HairConcern />} />
            <Route path="/hair/treatment" element={<HairAssessment />} />
            <Route path="/hair/treatment/prp-and-prf" element={<HairPrp />} />
            <Route path="/hair/transplantation" element={<TransplantHub />} />
            <Route path="/hair/transplantation/:subSlug" element={<TransplantSub />} />
            <Route path="/hair/dr-kristy-truong" element={<HairDoctor />} />
            <Route path="/hair/journal" element={<HairJournal />} />
            <Route path="/hair/assessment" element={<HairBooking />} />
            <Route path="/concerns/hair" element={<Navigate to="/hair/hair-loss" replace />} />
            <Route path="/concerns/hair/:slug" element={<LegacyHairConcernRedirect />} />
            <Route path="/hair-restoration" element={<Navigate to="/hair" replace />} />
            <Route path="/hair-restoration/assessment" element={<Navigate to="/hair/treatment" replace />} />
            <Route path="/hair-restoration/prp-and-prf" element={<Navigate to="/hair/treatment/prp-and-prf" replace />} />
            <Route path="/hair-restoration/consultation" element={<Navigate to="/hair/assessment" replace />} />
            <Route path="/hair-restoration/transplantation" element={<Navigate to="/hair/transplantation" replace />} />
            <Route path="/hair-restoration/transplantation/:subSlug" element={<HairTransplantRedirect />} />
            <Route path="/hair-assessment" element={<Navigate to="/hair/assessment" replace />} />
            <Route path="/first-visit" element={<FirstVisit />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/book" element={<Book />} />
            <Route path="/consult" element={<AdLanding page="consult" />} />
            <Route path="/skin-assessment" element={<AdLanding page="skin" />} />
            <Route path="/register" element={<Register />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<JournalPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Legal page="privacy" />} />
            <Route path="/terms" element={<Legal page="terms" />} />
            <Route path="/complaints" element={<Legal page="complaints" />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/booking-and-cancellation-policy" element={<Legal page="booking-and-cancellation-policy" />} />
            <Route path="/loyalty" element={<Legal page="loyalty" />} />
            <Route path="/services" element={<Navigate to="/treatments" replace />} />
            <Route path="/services/:slug" element={<Navigate to="/treatments" replace />} />
            <Route path="/doctors" element={<Navigate to="/team" replace />} />
            <Route path="/memberships" element={<Navigate to="/treatments/lumiere-private" replace />} />
            <Route path="/treatments/memberships" element={<Navigate to="/treatments/lumiere-private" replace />} />
            <Route path="/treatments/facials" element={<Navigate to="/treatments/skin-and-laser" replace />} />
            <Route path="/team/dr-shahid-jamil" element={<Navigate to="/team" replace />} />
            <Route path="/treatments/hair-restoration" element={<Navigate to="/hair" replace />} />
            <Route path="/hair-transplant" element={<Navigate to="/hair/transplantation" replace />} />
            <Route path="/hair-transplant/*" element={<HairTransplantRedirect />} />
            <Route path="/for-doctors" element={<Navigate to="/team" replace />} />
            <Route path="/gift-cards" element={<Navigate to="/pricing" replace />} />
            <Route path="/medical-disclaimer" element={<Navigate to="/terms" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
      </main>
      {!isBare && (isHair ? <HairFooter /> : <Footer />)}
      {!isBare && <div className="h-[68px] lg:hidden" aria-hidden="true" />}
      {!isAdmin && <CookieNotice />}
      {!isAdmin && <MetaPixel />}
      {!isAdmin && <GoogleAnalytics />}
      </ConciergeProvider>
      <Toaster position="bottom-center" />
    </>
  );
}

export default App;
